import { useState } from 'react';
import { useCategories, useCreateTransaction, useTransactions, useDeleteTransaction } from '@/hooks/useApi';
import { formatRupiah, toYMD } from '@/lib/format';

export default function TransactionModal({ onClose }) {
  const today = toYMD(new Date());
  const { data: categories = [] } = useCategories();
  const createTx = useCreateTransaction();
  const deleteTx = useDeleteTransaction();

  const [tab, setTab] = useState('expense'); // 'income' | 'expense'
  const [form, setForm] = useState({
    category_id: '',
    amount: '',
    date: today,
    description: '',
    is_recurring: false,
  });
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch this month's transactions for the list
  const firstOfMonth = new Date(); firstOfMonth.setDate(1);
  const { data: transactions = [], isLoading: txLoading } = useTransactions({
    from: toYMD(firstOfMonth),
    to: today,
  });

  const filteredCategories = categories.filter(c => c.type === tab);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSuccess('');
    if (!form.category_id) return setErr('Pilih kategori terlebih dahulu.');
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) return setErr('Masukkan nominal yang valid.');

    try {
      await createTx.mutateAsync({
        category_id: Number(form.category_id),
        amount: Number(form.amount),
        date: form.date,
        description: form.description,
        type: tab,
        is_recurring: form.is_recurring,
      });
      setSuccess('Transaksi berhasil dicatat!');
      setForm({ category_id: '', amount: '', date: today, description: '', is_recurring: false });
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menyimpan transaksi.');
    }
  };

  const handleDelete = async (id) => {
    setErr('');
    try {
      await deleteTx.mutateAsync(id);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menghapus transaksi.');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px',
      boxSizing: 'border-box'
    }}>
      <div style={{
        background: 'white', border: '2px solid var(--line)', boxShadow: '4px 4px 0 var(--line)',
        width: '100%', maxWidth: '500px', padding: 'clamp(16px, 4vw, 24px)', maxHeight: '90vh',
        overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <span className="panel-kicker">CATAT UANG</span>
            <h3 style={{ margin: '4px 0 0', font: "700 20px 'Space Grotesk'", letterSpacing: '-0.5px' }}>Input Transaksi</h3>
          </div>
          <button onClick={onClose} aria-label="Tutup" style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', lineHeight: 1, padding: '4px' }}>×</button>
        </div>

        {/* Tab type */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', borderBottom: '2px solid var(--line)', paddingBottom: '8px', marginBottom: '18px' }}>
          {[
            { id: 'expense', label: 'PENGELUARAN', activeBg: 'var(--red)' },
            { id: 'income', label: 'PEMASUKAN', activeBg: '#2e7d32' },
            { id: 'saving', label: 'TABUNGAN', activeBg: '#1565c0' },
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setForm(p => ({ ...p, category_id: '' })); }}
              style={{
                padding: '10px 4px', border: '1px solid var(--line)', cursor: 'pointer',
                background: tab === t.id ? t.activeBg : '#f9f8f6',
                color: tab === t.id ? 'white' : 'var(--ink)',
                font: "clamp(6.5px, 1.8vw, 8px) 'Press Start 2P'",
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: tab === t.id ? '2px 2px 0 var(--line)' : 'none'
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Alerts */}
        {filteredCategories.length === 0 && (
          <div style={{ background: '#fff6d7', border: '1px solid #edd78a', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', fontWeight: 700, color: '#7a5c00' }}>
            ⚠️ Belum ada kategori {tab === 'expense' ? 'pengeluaran' : tab === 'income' ? 'pemasukan' : 'tabungan'}. Tambahkan lewat "Kelola Kategori" dulu.
          </div>
        )}
        {err && (
          <div style={{ background: '#fff0ed', border: '1px solid var(--red)', padding: '10px 14px', marginBottom: '14px', color: 'var(--red)', fontSize: '12px', fontWeight: 700 }}>
            {err}
          </div>
        )}
        {success && (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', padding: '10px 14px', marginBottom: '14px', color: '#2e7d32', fontSize: '12px', fontWeight: 700 }}>
            ✅ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Full vertical inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ font: "7px 'Press Start 2P'", color: 'var(--muted)' }}>KATEGORI</label>
            <select
              value={form.category_id}
              onChange={(e) => setForm(p => ({ ...p, category_id: e.target.value }))}
              style={{
                width: '100%', border: '2px solid var(--line)', padding: '10px 12px',
                font: "600 13px 'Space Grotesk'", background: 'white', outline: 'none',
                boxSizing: 'border-box'
              }}
            >
              <option value="">-- Pilih Kategori --</option>
              {filteredCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ font: "7px 'Press Start 2P'", color: 'var(--muted)' }}>NOMINAL (Rp)</label>
            <input
              type="number"
              min="0"
              value={form.amount}
              onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
              placeholder="Contoh: 50000"
              style={{
                width: '100%', border: '2px solid var(--line)', padding: '10px 12px',
                font: "600 14px 'Space Grotesk'", outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ font: "7px 'Press Start 2P'", color: 'var(--muted)' }}>TANGGAL</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))}
              style={{
                width: '100%', border: '2px solid var(--line)', padding: '10px 12px',
                font: "600 13px 'Space Grotesk'", outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ font: "7px 'Press Start 2P'", color: 'var(--muted)' }}>DESKRIPSI (OPTIONAL)</label>
            <input
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Contoh: Nasi padang, beli kopi, dsb."
              style={{
                width: '100%', border: '2px solid var(--line)', padding: '10px 12px',
                font: "13px 'Space Grotesk'", outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', margin: '4px 0' }}>
            <input
              type="checkbox"
              checked={form.is_recurring}
              onChange={(e) => setForm(p => ({ ...p, is_recurring: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: 'var(--red)' }}
            />
            <span style={{ fontWeight: 600 }}>Transaksi rutin / berulang</span>
          </label>

          <button type="submit" disabled={createTx.isPending} className="primary-button"
            style={{
              background: 'var(--ink)', color: 'white', justifyContent: 'center',
              width: '100%', padding: '12px', fontSize: "9px"
            }}>
            {createTx.isPending ? 'MENYIMPAN...' : '+ SIMPAN TRANSAKSI'}
          </button>
        </form>

        {/* Recent transactions list */}
        <div style={{ marginTop: '24px', borderTop: '2px solid var(--line)', paddingTop: '16px' }}>
          <div style={{ font: "8px 'Press Start 2P'", color: 'var(--muted)', marginBottom: '12px' }}>TRANSAKSI BULAN INI</div>
          {txLoading ? (
            <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Memuat transaksi...</p>
          ) : transactions.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Belum ada transaksi bulan ini.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto', overflowX: 'hidden' }}>
              {transactions.slice(0, 30).map(tx => (
                <div key={tx.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  border: '1px solid #e8e5e0',
                  background: '#faf8f5',
                  gap: '8px',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <b style={{ font: "700 13px 'Space Grotesk'", color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.category?.name || '—'}
                      </b>
                      {tx.is_recurring && (
                        <span style={{ font: "6px 'Press Start 2P'", background: '#eceae4', padding: '2px 4px', border: '1px solid #ddd' }}>
                          RUTIN
                        </span>
                      )}
                    </div>
                    <span style={{ color: 'var(--muted)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.description || tx.date?.slice(0, 10)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <b style={{
                        display: 'block',
                        font: "700 13px 'Space Grotesk'",
                        color: tx.type === 'income' ? '#2e7d32' : tx.type === 'saving' ? '#1565c0' : 'var(--red)'
                      }}>
                        {tx.type === 'income' ? '+' : tx.type === 'saving' ? '🏦 ' : '-'}{formatRupiah(tx.amount)}
                      </b>
                      <span style={{ color: 'var(--muted)', fontSize: '10px' }}>{tx.date?.slice(0, 10)}</span>
                    </div>
                    <button onClick={() => handleDelete(tx.id)}
                      title="Hapus transaksi"
                      style={{
                        border: '1px solid #ffc5ba',
                        background: '#fff0ed',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        padding: '4px 6px',
                        lineHeight: 1
                      }}>
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
